import { KBIcon } from "./KBIcon"
import { ChevronDownIcon, PinIcon } from "lucide-react"
import { Link } from "~/components/Link"
import { Button } from "~/components/ui/button"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "~/components/ui/collapsible"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { cn } from "~/lib/utils"
import { api } from "~/trpc/react"

export function PinnedKBDocuments({
	teamId,
	orgSlug,
	teamSlug
}: {
	teamId: string
	orgSlug: string
	teamSlug: string
}) {
	const { hasTeamPermission } = usePermission({ activeTeamId: teamId })
	const canViewKb = hasTeamPermission(TeamPermissions.VIEW_KNOWLEDGE_BASE)
	const storageKey = `kb-pinned-collapsed:${teamId}`
	const collapsed = localStorage.getItem(storageKey) === "true"
	const documents = api.knowledgeBase.pinned.useQuery(
		{ teamId },
		{ enabled: canViewKb }
	)

	if (!canViewKb || !documents.data?.length) return null

	return (
		<Collapsible
			defaultOpen={!collapsed}
			onOpenChange={(open) => localStorage.setItem(storageKey, String(!open))}
			className="rounded-lg surface-raised bg-background-secondary p-3"
		>
			<div className="mb-2 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2 text-sm font-medium text-text-main">
					<span className="flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
						<PinIcon className="size-3.5" />
					</span>
					Pinned docs
					<span className="text-xs text-text-small">
						{documents.data.length}
					</span>
				</div>
				<CollapsibleTrigger asChild>
					<Button variant="ghost" size="icon-sm">
						<ChevronDownIcon className="size-4 transition-transform data-[state=open]:rotate-180" />
					</Button>
				</CollapsibleTrigger>
			</div>
			<CollapsibleContent>
				<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
					{documents.data.map((document) => (
						<Link
							key={document.id}
							to={`/${orgSlug}/${teamSlug}/kb/${document.slug}`}
							className={cn(
								"rounded-lg surface-raised bg-background-secondary px-3 py-2 transition hover:border-stroke-hover hover:bg-active"
							)}
						>
							<div className="flex min-w-0 items-center gap-2">
								<KBIcon
									name={document.icon}
									className="size-4 shrink-0 text-icons"
								/>
								<span className="truncate text-sm font-medium text-text-main">
									{document.title}
								</span>
							</div>
							{document.description ? (
								<p className="mt-1 truncate text-xs text-text-sub">
									{document.description}
								</p>
							) : null}
						</Link>
					))}
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
